import { useCallback, useEffect, useMemo, useState, type CSSProperties, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  locationsApi,
  type LocationKind,
  type LocationNode,
  type LocationRequest,
  type LocationView,
} from "../api/locations";
import type { ApiError } from "../api/client";
import { formatApiError } from "../lib/apiError";
import { LOCATION_INDENT as INDENT, flattenLocations, type FlatLocation } from "../lib/locationsFlat";
import { useLocationKinds } from "../lib/useLocationKinds";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../components/Toast";
import { usePrompt } from "../components/ConfirmDialog";
import { TreeView } from "../components/TreeView";
import { SplitHandle, useSplitWidth } from "../components/SplitHandle";
import { Modal } from "../components/Modal";
import { Select, type SelectOption } from "../components/Select";
import { AttachmentsPanel } from "../components/AttachmentsPanel";
import { CommentsPanel } from "../components/CommentsPanel";
import Ic from "../components/icons.jsx";
import { LocationKindIcon } from "../components/LocationKindIcon";
import { MapPicker, locateMe, searchPlace, type PlaceHit } from "../components/MapPicker";

type FormMode = { kind: "create"; parentId: string | null } | { kind: "edit"; location: LocationView };

const NO_PARENT = "__root__";

export default function LocationsPage() {
  const { t } = useTranslation();
  const split = useSplitWidth("bkm.split.locations");
  const toast = useToast();
  const prompt = usePrompt();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("locations:create");
  const canEdit = hasPermission("locations:edit");
  const canDelete = hasPermission("locations:delete");
  const { iconOf } = useLocationKinds();

  const [tree, setTree] = useState<LocationNode[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<LocationView | null>(null);
  const [form, setForm] = useState<FormMode | null>(null);

  const loadTree = useCallback(async () => {
    setError(null);
    try {
      setTree(await locationsApi.tree());
    } catch (err) {
      setError((err as ApiError).message);
    }
  }, []);

  useEffect(() => {
    void loadTree();
  }, [loadTree]);

  // Detail (path + effective GPS) comes from GET /locations/{id}, not the tree.
  useEffect(() => {
    if (!selectedId) {
      setSelected(null);
      return;
    }
    let cancelled = false;
    locationsApi
      .get(selectedId)
      .then((loc) => { if (!cancelled) setSelected(loc); })
      .catch((err: ApiError) => { if (!cancelled) toast.error(err.message); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const flat = useMemo(() => flattenLocations(tree ?? []), [tree]);

  async function handleDelete() {
    if (!selected) return;
    const reason = await prompt({
      title: t("locations.confirmDelete.title", "Brisanje lokacije"),
      message: t("locations.confirmDelete.message", "Obrisati lokaciju „{{name}}“ ({{code}})?", { name: selected.name, code: selected.code }),
      confirmLabel: t("action.delete", "Obriši"),
      placeholder: t("deleteReason.placeholder", "Razlog brisanja (obavezno) — npr. rashodovano, zamenjeno, greška pri unosu…"),
      multiline: true,
      required: true,
      danger: true,
    });
    if (!reason) return;
    try {
      await locationsApi.remove(selected.id, reason);
      toast.success(t("locations.toast.deleted", "Lokacija obrisana."));
      setSelectedId(selected.parentId);
      await loadTree();
    } catch (err) {
      const e = err as ApiError;
      if (e.code === "HAS_CHILDREN") {
        toast.error(t("locations.error.hasChildren", "Lokacija ima pod-lokacije — prvo obrišite ili premestite njih."));
      } else {
        toast.error(e.message);
      }
    }
  }

  async function handleSaved(saved: LocationView) {
    setForm(null);
    await loadTree();
    setSelectedId(saved.id);
    setSelected(saved);
  }

  const getId = useCallback((n: LocationNode) => n.id, []);
  const getLabel = useCallback((n: LocationNode) => n.name, []);
  const getChildren = useCallback((n: LocationNode) => n.children, []);

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>{t("locations.title", "Lokacije")}</h1>
          <div className="sub">{t("locations.subtitle", "Šifarnik fizičkih lokacija — gde se komad nalazi.")}</div>
        </div>
        {canCreate && (
          <div className="page-actions">
            <button type="button" className="btn primary" onClick={() => setForm({ kind: "create", parentId: null })}>
              <Ic.plus /> {t("locations.addRoot", "Dodaj korensku lokaciju")}
            </button>
          </div>
        )}
      </div>

      {error && <div style={errorBox} role="alert">{error}</div>}

      <div className="split-layout is-resizable" style={split.layoutStyle}>
        <aside className="card split-side" style={{ padding: 8 }}>
          {tree === null ? (
            <p className="muted" style={{ padding: 12, margin: 0 }}>{t("common.loading", "Učitavanje…")}</p>
          ) : (
            <TreeView<LocationNode>
              nodes={tree}
              getId={getId}
              getLabel={getLabel}
              getChildren={getChildren}
              selectedId={selectedId}
              onSelect={(n) => setSelectedId(n.id)}
              renderIcon={(n) => <LocationKindIcon icon={iconOf(n.kind)} />}
              renderBadge={(n) => <span className="tag" style={{ fontFamily: "var(--mono)" }}>{n.code}</span>}
              defaultExpandedDepth={99}
              ariaLabel={t("locations.title", "Lokacije")}
              emptyText={t("locations.empty", "Još nema lokacija. Dodajte prvu korensku lokaciju.")}
              expandLabel={t("tree.expand", "Proširi")}
              collapseLabel={t("tree.collapse", "Skupi")}
            />
          )}
        </aside>
        <SplitHandle {...split.handleProps} />

        <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
        <section className="card">
          {!selectedId ? (
            <div className="empty">
              <div>{t("locations.noneSelected", "Izaberite lokaciju u drvetu da vidite detalje.")}</div>
            </div>
          ) : !selected ? (
            <div className="empty">{t("common.loading", "Učitavanje…")}</div>
          ) : (
            <>
              <div className="card-h">
                <div style={{ minWidth: 0 }}>
                  <h3 style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <LocationKindIcon icon={iconOf(selected.kind)} />
                    {selected.name}
                  </h3>
                  <div className="sub">{selected.kindName} · <span style={{ fontFamily: "var(--mono)" }}>{selected.code}</span></div>
                </div>
                {(canCreate || canEdit || canDelete) && (
                  <div className="page-actions">
                    {canCreate && (
                      <button type="button" className="btn sm" onClick={() => setForm({ kind: "create", parentId: selected.id })}>
                        <Ic.plus /> {t("locations.addChild", "Dodaj pod-lokaciju")}
                      </button>
                    )}
                    {canEdit && (
                      <button type="button" className="btn sm" onClick={() => setForm({ kind: "edit", location: selected })}>
                        {t("action.edit", "Izmeni")}
                      </button>
                    )}
                    {canDelete && (
                      <button type="button" className="btn sm" style={dangerBtn} onClick={handleDelete}>
                        {t("action.delete", "Obriši")}
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="card-b">
                <dl className="kv">
                  <dt>{t("locations.field.code", "Šifra")}</dt>
                  <dd className="mono">{selected.code}</dd>
                  <dt>{t("locations.field.name", "Naziv")}</dt>
                  <dd>{selected.name}</dd>
                  <dt>{t("locations.field.kind", "Vrsta")}</dt>
                  <dd>{selected.kindName}</dd>
                  <dt>{t("locations.field.path", "Putanja")}</dt>
                  <dd>
                    {selected.path.length === 0
                      ? <span className="muted">{t("locations.rootNode", "(korenska lokacija)")}</span>
                      : selected.path.map((p) => p.name).join(" / ")}
                  </dd>
                  <dt>{t("locations.field.gps", "GPS")}</dt>
                  <dd>
                    {formatGps(selected.effectiveLat, selected.effectiveLng) ?? <span className="muted">—</span>}
                    {selected.lat === null && selected.effectiveLat !== null && (
                      <span className="muted" style={{ marginLeft: 6, fontSize: 12 }}>
                        {t("locations.gpsInheritedFrom", "(nasleđen od: {{name}})", { name: gpsSourceName(tree ?? [], selected.id) })}
                      </span>
                    )}
                  </dd>
                  <dt>{t("locations.field.sortOrder", "Redosled")}</dt>
                  <dd>{selected.sortOrder}</dd>
                  <dt>{t("locations.field.note", "Napomena")}</dt>
                  <dd style={{ whiteSpace: "pre-wrap" }}>{selected.note || <span className="muted">—</span>}</dd>
                </dl>
                {selected.effectiveLat !== null && selected.effectiveLng !== null && (
                  <div style={{ marginTop: 12 }}>
                    <MapPicker key={selected.id} lat={selected.effectiveLat} lng={selected.effectiveLng} readOnly height={220} />
                  </div>
                )}
              </div>
            </>
          )}
        </section>
        {/* Attachments + notes of the selected location (sprint 4) */}
        {selected && (
          <>
            <AttachmentsPanel
              entityKind="LOCATION"
              entityId={selected.id}
              entityCode={selected.code}
              title={t("locations.photos", "Fotografije")}
              allowedKinds={["PHOTO"]}
            />
            <CommentsPanel
              entityKind="LOCATION"
              entityId={selected.id}
              placeholder={t("locations.commentPlaceholder", "Napomena o lokaciji — kako se stiže, ključ/pristup, kontakt, šta se tu čuva…")}
            />
          </>
        )}
        </div>
      </div>

      {form && (
        <LocationFormModal
          mode={form}
          flat={flat}
          tree={tree ?? []}
          onClose={() => setForm(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function LocationFormModal({
  mode, flat, tree, onClose, onSaved,
}: {
  mode: FormMode;
  flat: FlatLocation[];
  tree: LocationNode[];
  onClose: () => void;
  onSaved: (saved: LocationView) => Promise<void>;
}) {
  const { t } = useTranslation();
  const editing = mode.kind === "edit" ? mode.location : null;
  const { kinds, loading: kindsLoading, iconOf } = useLocationKinds();
  const [code, setCode] = useState(editing?.code ?? "");
  const [name, setName] = useState(editing?.name ?? "");
  const [kind, setKind] = useState<LocationKind>(editing?.kind ?? "");
  const [parentId, setParentId] = useState<string>(
    (editing ? editing.parentId : mode.kind === "create" ? mode.parentId : null) ?? NO_PARENT,
  );
  const [lat, setLat] = useState(editing?.lat?.toString() ?? "");
  const [lng, setLng] = useState(editing?.lng?.toString() ?? "");
  const [note, setNote] = useState(editing?.note ?? "");
  const [sortOrder, setSortOrder] = useState(editing?.sortOrder?.toString() ?? "0");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showMap, setShowMap] = useState(false);
  const [locating, setLocating] = useState(false);
  const [placeQuery, setPlaceQuery] = useState("");
  const [placeHits, setPlaceHits] = useState<PlaceHit[] | null>(null);
  const [placeBusy, setPlaceBusy] = useState(false);

  const latNumLive = parseCoord(lat);
  const lngNumLive = parseCoord(lng);
  const mapLat = typeof latNumLive === "number" ? latNumLive : null;
  const mapLng = typeof lngNumLive === "number" ? lngNumLive : null;
  // Parent's effective GPS centers the map for a new child that has no point yet.
  const parentCenter = useMemo(
    () => (parentId === NO_PARENT ? null : effectiveCoords(tree, parentId)),
    [tree, parentId],
  );

  function applyCoords(c: { lat: number; lng: number }) {
    setLat(String(c.lat));
    setLng(String(c.lng));
  }

  async function handleLocateMe() {
    setLocating(true);
    setError(null);
    try {
      applyCoords(await locateMe());
      setShowMap(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLocating(false);
    }
  }

  async function handlePlaceSearch() {
    const q = placeQuery.trim();
    if (q.length < 2) return;
    setPlaceBusy(true);
    setError(null);
    try {
      setPlaceHits(await searchPlace(q));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setPlaceBusy(false);
    }
  }

  // A node cannot become its own descendant's child (409 CYCLE server-side; hide it client-side too).
  const parentOptions = useMemo<SelectOption<string>[]>(() => {
    const opts: SelectOption<string>[] = [{ value: NO_PARENT, label: t("locations.parentNone", "— bez roditelja (koren) —") }];
    for (const e of flat) {
      if (editing && (e.id === editing.id || e.ancestorIds.includes(editing.id))) continue;
      opts.push({ value: e.id, label: `${INDENT.repeat(e.depth)}${e.name} (${e.code})`, keywords: e.pathLabel, left: <LocationKindIcon icon={iconOf(e.kind)} /> });
    }
    return opts;
  }, [flat, editing, t, iconOf]);

  const kindOptions = useMemo<SelectOption<LocationKind>[]>(
    () => kinds.map((k) => ({ value: k.code, label: k.name })),
    [kinds],
  );

  // A new location defaults to the first admin-defined (non-system) kind, else OTHER.
  useEffect(() => {
    if (kind || kinds.length === 0) return;
    setKind(defaultKindCode(kinds));
  }, [kind, kinds]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const latNum = parseCoord(lat);
    const lngNum = parseCoord(lng);
    if (latNum === undefined || lngNum === undefined) {
      setError(t("locations.error.gps", "GPS koordinate moraju biti brojevi (npr. 44.6547 / 20.2003)."));
      return;
    }
    if ((latNum === null) !== (lngNum === null)) {
      setError(t("locations.error.gpsPair", "Unesite obe koordinate ili nijednu."));
      return;
    }
    if (!kind) {
      setError(t("locations.error.kindRequired", "Izaberite vrstu lokacije."));
      return;
    }
    const body: LocationRequest = {
      parentId: parentId === NO_PARENT ? null : parentId,
      code: code.trim(),
      name: name.trim(),
      kind,
      lat: latNum,
      lng: lngNum,
      note: note.trim() || null,
      sortOrder: Number.parseInt(sortOrder, 10) || 0,
    };
    setSubmitting(true);
    try {
      const saved = editing ? await locationsApi.update(editing.id, body) : await locationsApi.create(body);
      await onSaved(saved);
    } catch (err) {
      const ae = err as ApiError;
      if (ae.code === "CODE_TAKEN") setError(t("locations.error.codeTaken", "Šifra je već zauzeta."));
      else if (ae.code === "CYCLE") setError(t("locations.error.cycle", "Lokacija ne može biti ispod sopstvene pod-lokacije."));
      else setError(formatApiError(ae));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={editing ? t("locations.modal.editTitle", "Izmena lokacije") : t("locations.modal.createTitle", "Nova lokacija")}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div className="form-grid">
          <label className="field">
            <span>{t("locations.field.code", "Šifra")} <span className="req-mark">*</span></span>
            <input className="input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} required maxLength={40} placeholder="OB-MCC2" autoCapitalize="characters" style={{ fontFamily: "var(--mono)" }} />
          </label>
          <label className="field">
            <span>{t("locations.field.kind", "Vrsta")}</span>
            <Select<LocationKind>
              value={kind}
              options={kindOptions}
              onChange={setKind}
              disabled={kindsLoading}
              placeholder={kindsLoading ? t("common.loading", "Učitavanje…") : undefined}
              ariaLabel={t("locations.field.kind", "Vrsta")}
            />
          </label>
        </div>
        <label className="field">
          <span>{t("locations.field.name", "Naziv")} <span className="req-mark">*</span></span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} />
        </label>
        <label className="field">
          <span>{t("locations.field.parent", "Roditelj")}</span>
          <Select<string> value={parentId} options={parentOptions} onChange={setParentId} ariaLabel={t("locations.field.parent", "Roditelj")} />
        </label>
        <div className="form-grid">
          <label className="field">
            <span>{t("locations.field.lat", "Geo. širina (lat)")}</span>
            <input className="input" value={lat} onChange={(e) => setLat(e.target.value)} inputMode="decimal" placeholder="44.6547" />
          </label>
          <label className="field">
            <span>{t("locations.field.lng", "Geo. dužina (lng)")}</span>
            <input className="input" value={lng} onChange={(e) => setLng(e.target.value)} inputMode="decimal" placeholder="20.2003" />
          </label>
        </div>
        <div className="map-tools">
          <button type="button" className="btn sm" onClick={() => setShowMap((v) => !v)} aria-expanded={showMap}>
            <Ic.pin /> {showMap ? t("map.hide", "Sakrij mapu") : t("map.pick", "Izaberi na mapi")}
          </button>
          <button type="button" className="btn sm" onClick={handleLocateMe} disabled={locating}>
            {locating ? t("map.locating", "Traženje…") : t("map.locateMe", "Moja lokacija (GPS)")}
          </button>
          {(mapLat !== null || mapLng !== null) && (
            <button type="button" className="btn sm ghost" onClick={() => { setLat(""); setLng(""); }}>
              {t("map.clear", "Obriši koordinate")}
            </button>
          )}
        </div>
        {showMap && (
          <>
            <div className="map-tools">
              <div className="map-search">
                <input
                  className="input"
                  value={placeQuery}
                  onChange={(e) => { setPlaceQuery(e.target.value); setPlaceHits(null); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void handlePlaceSearch(); } }}
                  placeholder={t("map.searchPlaceholder", "Pronađi mesto: Šabac, Obrenovac, adresa…")}
                  aria-label={t("map.search", "Pretraga mesta")}
                />
                {placeHits && (
                  <div className="map-search-results" role="listbox">
                    {placeHits.length === 0 && <div className="map-hint" style={{ padding: 10 }}>{t("map.noHits", "Nema rezultata.")}</div>}
                    {placeHits.map((h) => (
                      <button type="button" key={`${h.lat},${h.lng}`} onClick={() => { applyCoords(h); setPlaceHits(null); }}>
                        {h.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button type="button" className="btn sm" onClick={handlePlaceSearch} disabled={placeBusy}>
                {placeBusy ? t("map.searching", "Tražim…") : t("map.search", "Pretraga mesta")}
              </button>
            </div>
            <MapPicker lat={mapLat} lng={mapLng} onChange={applyCoords} fallbackCenter={parentCenter} height={300} />
            <div className="map-hint">{t("map.hint", "Klikni na mapu ili prevuci marker — koordinate se upisuju u polja iznad. Ručni unos i dalje radi.")}</div>
          </>
        )}
        <div className="form-grid">
          <label className="field">
            <span>{t("locations.field.sortOrder", "Redosled")}</span>
            <input className="input" type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} inputMode="numeric" />
          </label>
        </div>
        <label className="field">
          <span>{t("locations.field.note", "Napomena")}</span>
          <textarea className="input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} style={textarea} />
        </label>
        {error && <div style={errorBox} role="alert">{error}</div>}
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>{t("action.cancel", "Otkaži")}</button>
          <button type="submit" className="btn primary" disabled={submitting}>
            {submitting ? t("action.saving", "Čuvanje…") : t("action.save", "Sačuvaj")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** First non-system kind (list is sorted by sortOrder), else OTHER, else the first one. */
export function defaultKindCode(kinds: { code: string; isSystem: boolean }[]): string {
  return kinds.find((k) => !k.isSystem)?.code ?? kinds.find((k) => k.code === "OTHER")?.code ?? kinds[0]?.code ?? "";
}

/** Own GPS of `id`, or the nearest ancestor's — mirrors the server's effectiveLat/Lng. */
function effectiveCoords(tree: LocationNode[], id: string): { lat: number; lng: number } | null {
  function walk(nodes: LocationNode[], chain: LocationNode[]): { lat: number; lng: number } | null | undefined {
    for (const n of nodes) {
      const next = [...chain, n];
      if (n.id === id) {
        for (let i = next.length - 1; i >= 0; i--) {
          const c = next[i];
          if (c.lat !== null && c.lng !== null) return { lat: c.lat, lng: c.lng };
        }
        return null;
      }
      const found = walk(n.children, next);
      if (found !== undefined) return found;
    }
    return undefined;
  }
  return walk(tree, []) ?? null;
}

/** Name of the nearest ancestor that carries its own GPS — the one an inherited point comes from. */
function gpsSourceName(tree: LocationNode[], id: string): string {
  function walk(nodes: LocationNode[], chain: LocationNode[]): string | null | undefined {
    for (const n of nodes) {
      const next = [...chain, n];
      if (n.id === id) {
        for (let i = next.length - 2; i >= 0; i--) {
          const c = next[i];
          if (c.lat !== null && c.lng !== null) return c.name;
        }
        return null;
      }
      const found = walk(n.children, next);
      if (found !== undefined) return found;
    }
    return undefined;
  }
  return walk(tree, []) ?? "";
}

function formatGps(lat: number | null, lng: number | null): string | null {
  if (lat === null || lng === null) return null;
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
}

/** "" → null, valid number → number, garbage → undefined. */
function parseCoord(raw: string): number | null | undefined {
  const s = raw.trim().replace(",", ".");
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
const dangerBtn: CSSProperties = { color: "var(--err)", borderColor: "rgba(182,53,58,.35)" };
const textarea: CSSProperties = { resize: "vertical", fontFamily: "inherit", width: "100%" };
